import { useMemo } from "react";
import { type ProductDeposit } from "@kanpla/api-contract";

import { type CreateOrderBasket } from "~/api/orders/requests";
import { autoId } from "~/utils/auto-id";

import { useBasketContext } from "../context";

export function useBasketProductsOrder(): CreateOrderBasket {
  const basket = useBasketContext((state) => state.basket);
  const productsInBasket = useBasketContext((state) => state.productsInBasket);
  const defaultTaxData = useBasketContext((state) => state.defaultTaxData);

  return useMemo(() => {
    return basket.flatMap((product) => {
      const productId = product.productId;

      if (product.type === "custom") {
        const customVatRate = product.customVatRate;
        const vatRate = customVatRate ? customVatRate : defaultTaxData.taxRate;

        const exclVatUnitPrice =
          product.vatCalculation === "excl"
            ? product.rawUnitPrice
            : product.unitPrice / (1 + vatRate);

        const productInBasket = productsInBasket.find(
          (p) => p.basketId === product.id
        );

        return {
          amount: product.count,
          category: product.category,
          unitPrice: exclVatUnitPrice,
          name: product.name,
          id: productId,
          type: product.type,
          productLineId: product.id,
          taxRateName: customVatRate ? null : defaultTaxData.taxRateName,
          taxRateId: customVatRate ? null : defaultTaxData.taxRateId,
          vatRate,
          ...(productInBasket?.discountUnitPrice && {
            unitDiscountedPrice: productInBasket.discountUnitPrice
          })
        };
      }

      const productInBasket = productsInBasket.find(
        (p) => p.basketId === product.id
      );

      if (!productInBasket) return [];

      return {
        amount: product.count,
        unitPrice: productInBasket.exclVatUnitPrice,
        vatRate: productInBasket.vatRate,
        category: productInBasket.category,
        productLineId: productInBasket.productLineId,
        name: productInBasket.name,
        description: productInBasket.description,
        id: productId,
        type: product.type,
        taxRateId: productInBasket.taxRateId,
        taxRateName: productInBasket.taxRateName,
        stampCardRuleId: productInBasket.stampCardRuleId,
        // Only the declarable half: the server owns name, externalId and how
        // VAT was applied, and re-resolves all of it.
        ...(productInBasket.deposit
          ? {
              deposit: {
                depositReturnSchemeId:
                  productInBasket.deposit.depositReturnSchemeId,
                feeExclVat: productInBasket.deposit.feeExclVat,
                feeInclVat: productInBasket.deposit.feeInclVat
              }
            }
          : {}),
        ...((product.type === "variant" ||
          product.type === "variant-weight") && {
          options: product.options.map((option) => ({
            id: "id" in option ? option.id : autoId(),
            choices: option.choices
              .filter((choice) => choice.amount > 0)
              .map((choice) => ({
                id: choice.id,
                amount: choice.amount,
                name: choice.name ?? "",
                ...("unitPrice" in choice && {
                  unitPrice: choice.unitPrice ?? 0
                }),
                ...("weight" in choice && { weight: choice.weight }),
                ...("weightUnit" in choice && {
                  weightUnit: choice.weightUnit
                }),
                ...("weightDecimals" in choice && {
                  weightDecimals: choice.weightDecimals
                }),
                ...declaredChoiceDeposit(
                  productInBasket.variantDeposits?.[choice.id]
                )
              }))
          }))
        }),
        ...(productInBasket.discountUnitPrice && {
          unitDiscountedPrice: productInBasket.discountUnitPrice
        }),
        // hybrid billing
        ...(productInBasket.discount && {
          discount: productInBasket.discount
        })
      };
    });
  }, [basket, productsInBasket, defaultTaxData]);
}

function declaredChoiceDeposit(deposit: ProductDeposit | undefined) {
  return (
    deposit && {
      deposit: {
        depositReturnSchemeId: deposit.depositReturnSchemeId,
        feeExclVat: deposit.feeExclVat,
        feeInclVat: deposit.feeInclVat
      }
    }
  );
}
